/**
 * Secure Token Manager
 * Handles JWT token storage and retrieval using memory + cookie fallback
 *
 * Security measures:
 * - Tokens stored in memory (cleared on page refresh)
 * - Optionally synced with HttpOnly cookies (backend-set)
 * - Never stored in localStorage or sessionStorage as primary storage
 */

let tokenInMemory = null;
let tokenExpiresAt = null;

/**
 * Get token from memory or retrieve from backend via refresh
 */
export const getToken = () => {
  if (tokenInMemory && tokenExpiresAt && Date.now() < tokenExpiresAt) {
    return tokenInMemory;
  }
  tokenInMemory = null;
  tokenExpiresAt = null;
  return null;
};

/**
 * Set token in memory with expiration tracking
 * The backend should send token in HttpOnly cookie for persistence
 */
export const setToken = (token, expiresIn = 3600) => {
  tokenInMemory = token;
  // Set expiration 1 minute before actual expiry for safety
  tokenExpiresAt = Date.now() + (expiresIn - 60) * 1000;
};

/**
 * Clear token from memory
 */
export const clearToken = () => {
  tokenInMemory = null;
  tokenExpiresAt = null;
};

/**
 * Check if token exists and is not expired
 */
export const hasValidToken = () => {
  return tokenInMemory !== null && tokenExpiresAt && Date.now() < tokenExpiresAt;
};

/**
 * Get time until token expires (in seconds)
 */
export const getTimeUntilExpiry = () => {
  if (!tokenExpiresAt) return 0;
  const secondsLeft = Math.floor((tokenExpiresAt - Date.now()) / 1000);
  return Math.max(0, secondsLeft);
};

/**
 * Refresh token if nearly expired (helper for axios interceptor)
 * Returns true if token was refreshed, false otherwise
 */
export const shouldRefreshToken = (thresholdSeconds = 300) => {
  const timeLeft = getTimeUntilExpiry();
  return timeLeft > 0 && timeLeft <= thresholdSeconds;
};

/**
 * Extract token from response (backend should set HttpOnly cookie too)
 * This extracts from response body if provided
 */
export const extractAndStoreToken = (responseData, expiresIn = 3600) => {
  if (responseData?.token) {
    setToken(responseData.token, expiresIn);
    return responseData.token;
  }
  return null;
};
